import * as React from "react";
import Image from "next/image";
import { GetFaQsByEventQuery, GetAlbumDetailsQuery } from "@src/@types/api.d";
import { useFaqList } from "@src/hooks/useFaqList";
import { motion, AnimatePresence } from "framer-motion";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import flowerBackground from "../../../../public/images/bw-flower.jpeg";

export type ITemplate10FAQ = {
  eventId: number;
  faqData: GetFaQsByEventQuery;
  albumDetailsData?: GetAlbumDetailsQuery;
  loading?: boolean;
};

const FAQ: React.FC<ITemplate10FAQ> = ({
  eventId,
  faqData,
  albumDetailsData,
}) => {
  const faqListRef = React.useRef<HTMLDivElement>(null);
  const faqSectionRef = React.useRef<HTMLDivElement>(null);
  const {
    faqItems,
    paginatedFaqItems,
    currentPage,
    totalPages,
    handlePageChange,
  } = useFaqList({ faqData, itemsPerPage: 3 });

  if (!faqItems.length) return null;

  const images = albumDetailsData?.getMedia ?? [];
  // cycle through available images as pages change
  const currentImage = images[(currentPage - 1) % images.length] ?? null;

  const goToPage = (page: number) => {
    handlePageChange(page);
    // scroll the faq section back to top
    setTimeout(() => {
      faqSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  return (
    <div
      ref={faqSectionRef}
      className="w-full py-8 lg:pb-12 overflow-hidden relative bg-[#E8EEF6]"
    >
      {/* Header */}
      <div className="text-center mb-10 px-6">
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="font-bauerBodoni text-2xl md:text-4xl font-light text-[#071A2F] uppercase tracking-wider"
        >
          Q & A
        </motion.h2>
      </div>

      {/* Card */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-20">
        <div className="relative">
          {/* Side chevrons — lg only */}
          {totalPages > 1 && (
            <>
              <button
                onClick={() => goToPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="hidden lg:flex absolute -left-[100px] top-1/2 -translate-y-1/2 z-10 w-[50px] h-[50px] rounded-full bg-[#031d40] items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 hover:bg-[#031d40]/90 active:scale-95"
                aria-label="Previous page"
              >
                <svg
                  className="w-4 h-4 rotate-180"
                  fill="none"
                  stroke="#b18958"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
              <button
                onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="hidden lg:flex absolute -right-[100px] top-1/2 -translate-y-1/2 z-10 w-[50px] h-[50px] rounded-full bg-[#031d40] items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 hover:bg-[#031d40]/90 active:scale-95"
                aria-label="Next page"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="#b18958"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </>
          )}

          <div className="border border-[#fff] p-2">
            <div className="bg-white border border-[#031d40]/20 shadow-xl flex flex-col lg:flex-row overflow-hidden">
              {/* Image — top on mobile, left on lg */}
              {currentImage && (
                <div className="w-full lg:w-1/2 flex-shrink-0">
                  <AnimatePresence exitBeforeEnter>
                    <motion.div
                      key={currentPage}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      className="relative w-full h-64 lg:h-full min-h-[320px]"
                    >
                      <Image
                        src={
                          currentImage?.meta?.s3_url ||
                          currentImage?.url ||
                          flowerBackground
                        }
                        alt={`FAQ image page ${currentPage}`}
                        layout="fill"
                        objectFit="cover"
                        placeholder="blur"
                        blurDataURL={`data:image/svg+xml;base64,${toBase64(
                          shimmer(600, 600)
                        )}`}
                        onError={(event) => {
                          const target =
                            event.currentTarget as HTMLImageElement;
                          if (!target.src.includes("bw-flower.jpeg")) {
                            target.src = flowerBackground.src;
                          }
                        }}
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>
              )}

              {/* FAQ list — right on lg, below on mobile */}
              <div className="flex flex-col flex-1 p-6 md:p-8 lg:p-10">
                <div
                  ref={faqListRef}
                  className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] space-y-6 min-h-[320px]"
                >
                  <AnimatePresence exitBeforeEnter>
                    <motion.div
                      key={currentPage}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.4 }}
                      className="space-y-6"
                    >
                      {paginatedFaqItems.map((item, index) => (
                        <div
                          key={item.id}
                          className="pb-6 border-b border-[#031d40]/20 last:border-b-0"
                        >
                          <h3 className="text-[#b18958] text-lg md:text-xl font-semibold leading-tight tracking-wide mb-2 font-bauerBodoni">
                            {item.question}
                          </h3>
                          <p className="text-[#071A2F] text-sm md:text-base leading-relaxed font-openSans opacity-80 whitespace-pre-line">
                            {item.answer}
                          </p>
                        </div>
                      ))}
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Pagination — pinned to bottom, mobile only for chevrons */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t border-[#031d40]/20">
                    <button
                      onClick={() => goToPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="lg:hidden w-[50px] h-[50px] rounded-full bg-[#031d40] flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 hover:bg-[#031d40]/90 active:scale-95"
                      aria-label="Previous page"
                    >
                      <svg
                        className="w-4 h-4 rotate-180"
                        fill="none"
                        stroke="#b18958"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>

                    <button
                      onClick={() =>
                        goToPage(Math.min(totalPages, currentPage + 1))
                      }
                      disabled={currentPage === totalPages}
                      className="lg:hidden w-[50px] h-[50px] rounded-full bg-[#031d40] flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed hover:scale-105 hover:bg-[#031d40]/90 active:scale-95"
                      aria-label="Next page"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="#b18958"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* end relative wrapper */}
      </div>
    </div>
  );
};

export default FAQ;
